import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Tooltip,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { FilteredStatuses, MeetingServiceOrder, TransactionResult } from "../../meetingServiceTypes"
import { StatusItem } from "../StatusMenu"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons"
import { faCircleExclamation } from "@fortawesome/pro-light-svg-icons"

const useStyles = makeStyles()((theme) => ({
    tableContainer: {
        marginBottom: theme.spacing(3),
        marginTop: theme.spacing(2),
    },
    warningIcon: {
        color: theme.palette.tailwind.red[500],
        marginRight: theme.spacing(1),
    },
    orderRow: {
        transition: "background-color .3s linear",
        height: "5rem",
    },
    failedOrderRow: {
        background: theme.palette.tailwind.slate[50],
    },
    warningCell: {
        width: "2rem",
    },
    tableHeader: {
        background: theme.palette.tailwind.slate[50],
    },
    orderStatus: {
        width: "fit-content",
    },
    orderMeetingName: {
        color: theme.palette.tailwind.slate[700],
        fontSize: "0.875rem",
    },
    orderCustomerName: {
        color: theme.palette.tailwind.slate[700],
        fontFamily: "Poppins, sans-serif",
        fontSize: "0.625rem",
    },
    centeredCell: {
        display: "flex",
        justifyContent: "center",
    },
    tableHeaderCell: {
        color: theme.palette.tailwind.slate[500],
        fontSize: "0.875rem",
    },
    tableDateCell: {
        color: theme.palette.tailwind.slate[700],
        fontSize: "0.875rem",
    },
}))

interface OrdersTableProps {
    orders: MeetingServiceOrder[]
    status: FilteredStatuses
    batchResponse?: TransactionResult[]
}

export function OrdersStatusTable({ orders, status, batchResponse }: OrdersTableProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    return (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
            <Table>
                <TableHead className={classes.tableHeader}>
                    <TableRow>
                        <TableCell className={classes.warningCell} />
                        <TableCell className={classes.tableHeaderCell}>
                            <LocalizedStrict id="meeting-service-batch-modal-order-id">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    Order ID
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell}>
                            <LocalizedStrict id="meeting-service-batch-modal-delivery-time">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    Delivery time
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell}>
                            <LocalizedStrict id="meeting-service-batch-modal-order-details">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    Order details
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell align="center" className={classes.tableHeaderCell}>
                            <LocalizedStrict id="meeting-service-batch-modal-from">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    From
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell />
                        <TableCell align="center" className={classes.tableHeaderCell}>
                            <LocalizedStrict id="meeting-service-batch-modal-to">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    To
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {orders.map((order) => {
                        const dateTime = dateTimeFactory.fromApiOrUndefined(orders[0].deliveryDetails.time)
                        const transactionOrder = batchResponse?.find(
                            ({ order: batchOrder }) => batchOrder?.id === order.id,
                        )
                        const transactionOrderError =
                            transactionOrder?.status.code !== 200
                                ? transactionOrder?.order?.deliveryDetails?.statusDetails
                                : null
                        const disallowedState =
                            transactionOrderError?.message ?? order.orderAction.statusTransitions?.disallowed?.[status]
                        const allowedState =
                            order.orderAction.statusTransitions?.allowed?.[status] ||
                            Object.values(order.orderAction.statusTransitions?.allowed ?? {})[0]

                        return (
                            <Tooltip title={allowedState ?? disallowedState}>
                                <TableRow
                                    key={order.id}
                                    className={cx(classes.orderRow, disallowedState && classes.failedOrderRow)}
                                >
                                    <TableCell className={classes.warningCell}>
                                        {disallowedState && (
                                            <FontAwesomeIcon
                                                size="lg"
                                                icon={faCircleExclamation}
                                                className={classes.warningIcon}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell className={classes.tableDateCell}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2">{order.id}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" className={classes.tableDateCell}>
                                            {dateTime ? dateTimeFactory.formatDateTime(dateTime) : "-"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body1" className={classes.orderMeetingName}>
                                                {order.meetingDetails.meetingName}
                                            </Typography>
                                            <Typography variant="body1" className={classes.orderCustomerName}>
                                                {order.customer.displayName}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box className={classes.centeredCell}>
                                            <StatusItem
                                                variant="state"
                                                className={classes.orderStatus}
                                                status={
                                                    transactionOrderError?.status ??
                                                    order.deliveryDetails.statusDetails.status
                                                }
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box className={classes.centeredCell}>
                                            <StatusItem
                                                variant="state"
                                                className={classes.orderStatus}
                                                status={status}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </Tooltip>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
